<template>
  <div>
    <ValidationObserver ref="general-information-form">
      <GeneralInformation
        v-model="model"
        :collections="collections.academicYears"
        :is-read-only="isReadOnly"
        :is-phone-required="isPhoneRequired"
      />
    </ValidationObserver>

    <ValidationObserver ref="request-information-form">
      <SectionTitle
        class="my-3 mt-2"
        :has-line="true"
        :title="'Información de Solicitud'"
      />
      <div
        v-for="(item, index) in collections.necessities"
        :key="index"
        class="mt-3"
      >
        <CustomCheckbox
          :ref="'checkbox-' + item.name"
          :text="item.name"
          :disabled="isReadOnly"
          class="ml-2 ml-sm-3 mb-3"
          @input="(val) => necessitiesChange(val, item)"
        />
        <div class="dropdown-divider my-0 w-100" />
        <div v-if="item.selected">
          <!--Hygíene-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.hygiene)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            :control-name="'Hygiene'"
            :show-text-area-divider="!isGroupStudent"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-slot:content v-if="canShowNecessitySubSections">
              <template v-if="isSingleStudent">
                <Document
                  v-for="(document, i) in getNecessity(item.id)
                    .studentNecessities[0].documents"
                  :key="i"
                  v-model="document.file"
                  :attachment-id.sync="document.itemAttachmentId"
                  :filename="document.filename"
                  :disabled="isReadOnly"
                  :label-value="document.documentTypeName"
                  :control-name="`${document.documentTypeName} ${item.name} ${i}`"
                  :rules="document.rules"
                  :accept="'.pdf'"
                  :max-size="10"
                  label="Documento:"
                />
              </template>

              <template v-if="isTwoMoreStudent">
                <div v-if="!isReadOnly" class="row mb-3">
                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>

                  <div class="col-12 col-md-4">
                    <CustomDropDown
                      v-model="studentToAdd.hygiene"
                      :options="collections.hygieneStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="studentSie"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed custom-ddl-error-no-height"
                    />
                  </div>

                  <div class="col-12 col-md-4 mt-md-auto mt-2">
                    <ActionButton
                      v-if="!isReadOnly"
                      text="Añadir"
                      variant="success my-auto"
                      i="fas fa-file-plus"
                      class="add-health-assistance"
                      @click="
                        addStudentNeccesity(
                          item,
                          studentToAdd.hygiene,
                          collections.hygieneStudent.list,
                          () => (studentToAdd.hygiene = null)
                        )
                      "
                    />
                  </div>

                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>
                </div>

                <div
                  v-for="(studentNecessity, i) in getNecessity(item.id)
                    .studentNecessities"
                  :key="i"
                >
                  <div class="row mt-2 bg-white">
                    <div class="col-12 col-md-4">
                      <div class="form-document-container py-2 px-3 mt-2">
                        <label class="grey-text">Estudiante:</label>
                        <p>{{ studentNecessity.student.name }}</p>
                      </div>
                    </div>
                    <div
                      v-for="(document, x) in studentNecessity.documents"
                      :key="x"
                      class="col-12 col-md-8"
                    >
                      <Document
                        v-model="document.file"
                        :attachment-id.sync="document.itemAttachmentId"
                        :filename="document.filename"
                        :disabled="isReadOnly"
                        :label-value="document.documentTypeName"
                        :control-name="`${document.documentTypeName} ${item.name} ${i}`"
                        :rules="document.rules"
                        :accept="'.pdf'"
                        :max-size="10"
                        label="Documento:"
                        class="mt-2"
                        display-style="normal"
                      />
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </NecessityInformationForm>

          <!--Feeding-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.feeding)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            :control-name="'Feeding'"
            :show-text-area-divider="!isGroupStudent"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-slot:content v-if="canShowNecessitySubSections">
              <template v-if="isSingleStudent">
                <Document
                  v-for="(document, i) in getNecessity(item.id)
                    .studentNecessities[0].documents"
                  :key="i"
                  v-model="document.file"
                  :attachment-id.sync="document.itemAttachmentId"
                  :filename="document.filename"
                  :disabled="isReadOnly"
                  :label-value="document.documentTypeName"
                  :control-name="`${document.documentTypeName} ${item.name} ${i}`"
                  :rules="document.rules"
                  :class="{ 'mt-2': i !== 0 }"
                  :accept="'.pdf'"
                  :max-size="10"
                  label="Documento:"
                />
              </template>
              <template v-if="isTwoMoreStudent">
                <div v-if="!isReadOnly" class="row mb-3">
                  <div class="col-12 col-md-4">
                    <CustomDropDown
                      v-model="studentToAdd.feeding"
                      :options="collections.feedingStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="studentSie"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed custom-ddl-error-no-height"
                    />
                  </div>

                  <div class="col-12 col-md-4 mt-md-auto mt-2">
                    <ActionButton
                      v-if="!isReadOnly"
                      text="Añadir"
                      variant="success my-auto"
                      i="fas fa-file-plus"
                      class="add-health-assistance"
                      @click="
                        addStudentNeccesity(
                          item,
                          studentToAdd.feeding,
                          collections.feedingStudent.list,
                          () => (studentToAdd.feeding = null)
                        )
                      "
                    />
                  </div>

                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>
                </div>

                <div class="row">
                  <div
                    v-for="(studentNecessity, i) in getNecessity(item.id)
                      .studentNecessities"
                    :key="i"
                    class="col-12"
                  >
                    <custom-callout
                      :id="'feeding-callout-' + studentNecessity.studentSieId"
                      :has-toggle="true"
                      class="mt-2 mb-3 bg-white"
                      :opened="true"
                      :controlled="true"
                      @click:open="
                        toggleCallout(
                          'feeding-callout-' + studentNecessity.studentSieId
                        )
                      "
                    >
                      <div class="container-fluid">
                        <div class="row">
                          <div class="col-12 mb-2 mb-md-0">
                            <span class="box-result-field-label">
                              Estudiante:
                            </span>
                            <br />
                            <span
                              class="box-result-field-value font-weight-bold"
                            >
                              {{ studentNecessity.student.name }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <template #edition>
                        <div
                          v-if="studentNecessity.documents"
                          class="container-fluid callout-expanded-container white-container"
                        >
                          <div class="row callout-expanded-item p-2">
                            <div class="col-12">
                              <document-selector
                                ref="documentSelectorRef"
                                :value="studentNecessity.documents"
                                :disabled="isReadOnly"
                                @on-selected-document="
                                  onSelectedDocumentHandler(
                                    studentNecessity,
                                    $event
                                  )
                                "
                              />
                            </div>
                          </div>
                        </div>
                      </template>
                    </custom-callout>
                  </div>
                </div>
              </template>
            </template>
          </NecessityInformationForm>

          <!--Mobility-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.movility)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            :control-name="'Movility'"
            :show-text-area-divider="false"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-if="isTwoMoreStudent && canShowNecessitySubSections" v-slot:content>
              <div class="row">
                <div v-if="!isReadOnly" class="col-12 col-md-4 mt-2">
                  <div
                    :class="{
                      'divider-horizontal pr-3': !isMobile,
                      'divider-vertical pt-3': isMobile,
                    }"
                  >
                    <CustomDropDown
                      v-model="studentToAdd.mobility"
                      :options="collections.mobilityStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="studentSie"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                    />
                    <div
                      class="col-12 px-0 pt-3 text-right mb-2 d-flex align-self-end justify-content-end"
                    >
                      <ActionButton
                        v-if="!isReadOnly"
                        text="Añadir"
                        variant="success"
                        i="fas fa-file-plus"
                        class="add-tran"
                        @click="
                          addStudentNeccesity(
                            item,
                            studentToAdd.mobility,
                            collections.mobilityStudent.list,
                            () => (studentToAdd.mobility = null)
                          )
                        "
                      />
                    </div>
                  </div>
                </div>
                <div :class="['col-12', { 'col-md-8 mt-2': !isReadOnly }]">
                  <div class="row">
                    <div class="col-12 ml-3 chips-container">
                      <CustomChipsGroup
                        :selected-items="
                          getNecessity(item.id).studentNecessities
                        "
                        :disabled="isReadOnly"
                        title="Estudiantes seleccionados"
                        :no-background="true"
                        :no-wrap="true"
                        object-text="name"
                        @itemMarkedForDeletion="
                          (studentNecessity) =>
                            removeStudentNeccesity(
                              item,
                              studentNecessity,
                              collections.mobilityStudent.list
                            )
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </NecessityInformationForm>

          <!--Comunication-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.comunication)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            title="El área de asistencia seleccionada debe estar acorde a las necesidades que serán atendidas en el ámbito escolar del Tab III (A y B) del PEI."
            :control-name="'Comunication'"
            :show-text-area-divider="false"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-if="isTwoMoreStudent && canShowNecessitySubSections" v-slot:content>
              <div class="row">
                <div v-if="!isReadOnly" class="col-12 col-md-4 mt-2">
                  <div
                    :class="{
                      'divider-horizontal pr-3': !isMobile,
                      'divider-vertical pt-3': isMobile,
                    }"
                  >
                    <CustomDropDown
                      v-model="studentToAdd.communication"
                      :options="collections.communicationStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="studentSie"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                    />
                    <div
                      class="col-12 px-0 pt-3 text-right mb-2 d-flex align-self-end justify-content-end"
                    >
                      <ActionButton
                        v-if="!isReadOnly"
                        text="Añadir"
                        variant="success"
                        i="fas fa-file-plus"
                        class="add-tran"
                        @click="
                          addStudentNeccesity(
                            item,
                            studentToAdd.communication,
                            collections.communicationStudent.list,
                            () => (studentToAdd.communication = null)
                          )
                        "
                      />
                    </div>
                  </div>
                </div>
                <div :class="['col-12', { 'col-md-8 mt-2': !isReadOnly }]">
                  <div class="row">
                    <div class="col-12 ml-3 chips-container">
                      <CustomChipsGroup
                        :selected-items="
                          getNecessity(item.id).studentNecessities
                        "
                        :disabled="isReadOnly"
                        title="Estudiantes seleccionados"
                        :no-background="true"
                        :no-wrap="true"
                        object-text="name"
                        @itemMarkedForDeletion="
                          (studentNecessity) =>
                            removeStudentNeccesity(
                              item,
                              studentNecessity,
                              collections.communicationStudent.list
                            )
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </NecessityInformationForm>

          <!--Health-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.health)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            :control-name="'Health'"
            :show-text-area-divider="false"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-slot:content v-if="canShowNecessitySubSections">
              <template v-if="isSingleStudent">
                <div v-if="!isReadOnly" class="row">
                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>

                  <div class="col-12 col-md-4">
                    <CustomDropDown
                      v-model="healthAssistanceTypeToAdd"
                      :options="healthAssistanceTypes"
                      :disabled="isReadOnly"
                      :clearable="false"
                      label="Tipo de Asistencia de Salud:"
                      fieldkey="id"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                    />
                  </div>

                  <div class="col-12 col-md-4 mt-md-auto mt-2">
                    <ActionButton
                      v-if="!isReadOnly"
                      text="Añadir"
                      variant="success my-auto"
                      i="fas fa-file-plus"
                      class="add-health-assistance"
                      @click="
                        addStudentNeccesityWithAssistance(
                          item,
                          model.studentList[0],
                          null,
                          healthAssistanceTypeToAdd,
                          healthAssistanceTypes,
                          () => (healthAssistanceTypeToAdd = null)
                        )
                      "
                    />
                  </div>

                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>
                </div>

                <div class="row">
                  <div
                    v-for="(assistantType, i) in getNecessity(item.id)
                      .studentNecessities[0].assistantTypes"
                    :key="i"
                    class="col-12"
                  >
                    <Document
                      v-if="
                        assistantType.id ===
                        peiServiceNeedCodes.healthAssistantCateterism
                      "
                      v-model="
                        getNecessity(item.id).studentNecessities[0].documents[0]
                          .file
                      "
                      :attachment-id.sync="
                        getNecessity(item.id).studentNecessities[0].documents[0]
                          .itemAttachmentId
                      "
                      :filename="
                        getNecessity(item.id).studentNecessities[0].documents[0]
                          .filename
                      "
                      :rules="
                        getNecessity(item.id).studentNecessities[0].documents[0]
                          .rules
                      "
                      :control-name="`${
                        getNecessity(item.id).studentNecessities[0].documents[0]
                          .documentTypeName
                      } ${item.name} ${i}`"
                      :disabled="isReadOnly"
                      :label-value="assistantType.name"
                      label="Tipo de Asistencia de Servicio"
                      class="px-3 my-2"
                      :is-health-assistance="true"
                    >
                      <template v-slot:actions>
                        <div class="d-flex justify-content-end mt-sm-0 mt-2">
                          <ActionButton
                            v-if="!isReadOnly"
                            :class="{ 'w-100': isMobile }"
                            variant="outline-danger"
                            i="fas fa-minus-circle"
                            text="Eliminar"
                            @click="
                              removeStudentNeccesityWithAssistance(
                                item,
                                model.studentList[0],
                                null,
                                assistantType,
                                healthAssistanceTypes
                              )
                            "
                          />
                        </div>
                      </template>
                    </Document>

                    <div v-else class="row bg-white mx-auto my-2">
                      <div class="col-12 col-md-10 p-3">
                        <div class="d-flex flex-column">
                          <label
                            class="font-weight-normal assistant-type-label"
                          >
                            Tipo de Asistencia de Servicio:
                          </label>
                          <span class="font-weight-normal">
                            {{ assistantType.name }}
                          </span>
                        </div>
                      </div>
                      <div class="col-12 col-md-2 mb-auto mt-auto">
                        <div class="d-flex justify-content-end">
                          <ActionButton
                            v-if="!isReadOnly"
                            :class="{ 'w-100': isMobile }"
                            variant="outline-danger"
                            i="fas fa-minus-circle"
                            text="Eliminar"
                            @click="
                              removeStudentNeccesityWithAssistance(
                                item,
                                model.studentList[0],
                                null,
                                assistantType,
                                healthAssistanceTypes
                              )
                            "
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-if="isTwoMoreStudent">
                <div v-if="!isReadOnly" class="row mb-3">
                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>

                  <div class="col-12 col-md-5">
                    <CustomDropDown
                      v-model="studentToAdd.health"
                      :options="collections.healthStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="peiId"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                      @input="
                        (student) => {
                          healthAssistanceTypes = getAssistanceTypes(
                            item,
                            student.studentSie,
                            getServiceNeeds(student.serviceNeeds, item.id)
                          );
                          healthAssistanceTypeToAdd = null;
                        }
                      "
                    />
                  </div>

                  <div class="col-12 col-md-5">
                    <CustomDropDown
                      v-model="healthAssistanceTypeToAdd"
                      :options="healthAssistanceTypes"
                      :disabled="isReadOnly"
                      label="Tipo de Asistencia de Salud:"
                      fieldkey="id"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                      :clearable="false"
                    />
                  </div>

                  <div class="col-12 col-md-2 mt-md-auto mt-2">
                    <ActionButton
                      v-if="!isReadOnly"
                      text="Añadir"
                      variant="success my-auto"
                      i="fas fa-file-plus"
                      class="add-health-assistance"
                      @click="
                        addStudentNeccesityWithAssistance(
                          item,
                          studentToAdd.health,
                          collections.healthStudent.list,
                          healthAssistanceTypeToAdd,
                          healthAssistanceTypes,
                          () => {
                            healthAssistanceTypeToAdd = null;
                            studentToAdd.health = null;
                            healthAssistanceTypes = [];
                          }
                        )
                      "
                    />
                  </div>

                  <div class="col-12 d-md-none d-block my-2 my-md-0">
                    <div class="my-2 my-md-4 form-divider" />
                  </div>
                </div>

                <div class="row">
                  <div
                    v-for="(studentNecessity, i) in getNecessity(item.id)
                      .studentNecessities"
                    :key="i"
                    class="col-12"
                  >
                    <custom-callout
                      :id="'health-callout-' + studentNecessity.studentSieId"
                      :has-toggle="true"
                      class="mt-2 mb-3 bg-white"
                      :opened="true"
                      :controlled="true"
                      @click:open="
                        toggleCallout(
                          'health-callout-' + studentNecessity.studentSieId
                        )
                      "
                    >
                      <div class="container-fluid">
                        <div class="row">
                          <div class="col-12 col-md-10 mb-2 mb-md-0">
                            <span class="box-result-field-label">
                              Estudiante:
                            </span>
                            <br />
                            <span
                              class="box-result-field-value font-weight-bold"
                            >
                              {{ studentNecessity.student.name }}
                            </span>
                          </div>
                          <div class="col-12 col-md-2 mt-auto">
                            <div class="d-flex justify-content-end">
                              <ActionButton
                                v-if="!isReadOnly"
                                :class="{ 'w-100': isMobile }"
                                variant="outline-danger"
                                i="fas fa-minus-circle"
                                text="Eliminar"
                                @click="
                                  removeStudentNeccesity(
                                    item,
                                    studentNecessity,
                                    collections.healthStudent.list
                                  )
                                "
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <template #edition>
                        <div
                          v-if="studentNecessity.assistantTypes"
                          class="container-fluid callout-expanded-container white-container"
                        >
                          <div class="row callout-expanded-item p-2">
                            <div
                              v-for="(
                                assistantType, x
                              ) in studentNecessity.assistantTypes"
                              :key="x"
                              class="col-12 grey-background"
                            >
                              <Document
                                v-if="
                                  assistantType.id ===
                                  peiServiceNeedCodes.healthAssistantCateterism
                                "
                                v-model="studentNecessity.documents[0].file"
                                :attachment-id.sync="
                                  studentNecessity.documents[0].itemAttachmentId
                                "
                                :filename="
                                  studentNecessity.documents[0].filename
                                "
                                :disabled="isReadOnly"
                                :label-value="assistantType.name"
                                :control-name="`${studentNecessity.documents[0].documentTypeName} ${item.name} ${i}`"
                                label="Tipo de Asistencia de Servicio"
                                class="px-3 my-2"
                                :is-health-assistance="true"
                              />

                              <div v-else class="row px-3 py-2">
                                <div
                                  class="col-12 p-3 d-flex flex-column bg-white"
                                >
                                  <label
                                    class="font-weight-normal assistant-type-label mt-2"
                                  >
                                    Tipo de Asistencia de Servicio:
                                  </label>
                                  <span class="font-weight-normal">
                                    {{ assistantType.name }}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </custom-callout>
                  </div>
                </div>
              </template>
            </template>
          </NecessityInformationForm>

          <!--Transportation-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.transportation)"
            v-model="getNecessity(item.id).justification"
            :disabled="isReadOnly"
            :control-name="'Transportation'"
            :show-title="false"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-slot:content v-if="canShowNecessitySubSections">
              <template v-if="isSingleStudent">
                <div class="row">
                  <div v-if="!isReadOnly" class="col-12 col-md-4 mt-2">
                    <div
                      :class="{
                        'divider-horizontal pr-3': !isMobile,
                        'divider-vertical pt-3': isMobile,
                      }"
                    >
                      <CustomDropDown
                        v-model="serviceAssistanceTypeToAdd"
                        :options="serviceAssistanceTypes"
                        :disabled="isReadOnly"
                        :clearable="false"
                        label="Tipo de Asistencia de Servicio:"
                        fieldkey="id"
                        fieldname="name"
                        placeholder="Seleccione"
                        class="height-fixed"
                      />
                      <div
                        class="col-12 px-0 pt-3 text-right mb-2 d-flex align-self-end justify-content-end"
                      >
                        <ActionButton
                          v-if="!isReadOnly"
                          text="Añadir"
                          variant="success"
                          i="fas fa-file-plus"
                          class="add-tran"
                          @click="
                            addStudentNeccesityWithAssistance(
                              item,
                              model.studentList[0],
                              null,
                              serviceAssistanceTypeToAdd,
                              serviceAssistanceTypes,
                              () => (serviceAssistanceTypeToAdd = null)
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div :class="['col-12', { 'col-md-8 mt-2': !isReadOnly }]">
                    <div class="row">
                      <div class="col-12 ml-3 chips-container">
                        <CustomChipsGroup
                          :selected-items="
                            getNecessity(item.id).studentNecessities[0]
                              .assistantTypes
                          "
                          :disabled="isReadOnly"
                          title="Tipo de Asistencia de Servicio seleccionados"
                          :no-background="true"
                          :no-wrap="true"
                          object-text="name"
                          @itemMarkedForDeletion="
                            (assistance) =>
                              removeStudentNeccesityWithAssistance(
                                item,
                                model.studentList[0],
                                null,
                                assistance,
                                serviceAssistanceTypes
                              )
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
              <template v-if="isTwoMoreStudent">
                <div class="row">
                  <div v-if="!isReadOnly" class="col-12 col-md-4 mt-2">
                    <div
                      :class="{
                        'divider-horizontal pr-3': !isMobile,
                        'divider-vertical pt-3': isMobile,
                      }"
                    >
                      <div>
                        <CustomDropDown
                          v-model="studentToAdd.transportation"
                          :options="collections.transportationStudent.list"
                          :disabled="isReadOnly"
                          :clearable="false"
                          :error="isValidNeccesity(item)"
                          :custom-error-msg="errorMsgNoStudentsAdded"
                          label="Nombre de Estudiante:"
                          fieldkey="peiId"
                          fieldname="name"
                          placeholder="Seleccione"
                          class="height-fixed"
                          @input="
                            (student) => {
                              serviceAssistanceTypes = getAssistanceTypes(
                                item,
                                student.studentSie,
                                getServiceNeeds(student.serviceNeeds, item.id)
                              );
                              serviceAssistanceTypeToAdd = null;
                            }
                          "
                        />
                      </div>
                      <CustomDropDown
                        v-model="serviceAssistanceTypeToAdd"
                        :options="serviceAssistanceTypes"
                        :disabled="isReadOnly"
                        label="Tipo de Asistencia de Servicio:"
                        fieldkey="id"
                        fieldname="name"
                        placeholder="Seleccione"
                        class="height-fixed"
                        :clearable="false"
                      />
                      <div
                        class="col-12 px-0 pt-3 text-right mb-2 d-flex align-self-end justify-content-end"
                      >
                        <ActionButton
                          v-if="!isReadOnly"
                          text="Añadir"
                          variant="success"
                          i="fas fa-file-plus"
                          class="add-tran"
                          @click="
                            addStudentNeccesityWithAssistance(
                              item,
                              studentToAdd.transportation,
                              collections.transportationStudent.list,
                              serviceAssistanceTypeToAdd,
                              serviceAssistanceTypes,
                              () => {
                                serviceAssistanceTypeToAdd = null;
                                studentToAdd.transportation = null;
                                serviceAssistanceTypes = [];
                              }
                            )
                          "
                        />
                      </div>
                    </div>
                  </div>
                  <div :class="['col-12', { 'col-md-8 mt-2': !isReadOnly }]">
                    <div class="row">
                      <div class="col-12 ml-3 chips-container">
                        <CustomChipsGroup
                          :selected-items="selectedItemTransportacion"
                          :disabled="isReadOnly"
                          title="Tipo de Asistencia de Servicio seleccionados"
                          no-background
                          object-text="assitenceName"
                          object-title="studentName"
                          @itemMarkedForDeletion="
                            (selectedItem) =>
                              removeStudentNeccesityWithAssistance(
                                item,
                                selectedItem.student,
                                collections.transportationStudent.list,
                                selectedItem.assitence
                              )
                          "
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>
          </NecessityInformationForm>

          <!--Others-->
          <NecessityInformationForm
            v-if="canShowNecessityInformationForm(item.id, necessityCodes.others)"
            v-model="getNecessity(item.id).justification"
            control-name="Others"
            :disabled="isReadOnly"
            :show-text-area-divider="false"
            :show-title="false"
            :is-justification-required="areJustificationsRequired"
          >
            <template v-if="isTwoMoreStudent && canShowNecessitySubSections" v-slot:content>
              <div class="row">
                <div v-if="!isReadOnly" class="col-12 col-md-4 mt-2">
                  <div
                    :class="{
                      'divider-horizontal pr-3': !isMobile,
                      'divider-vertical pt-3': isMobile,
                    }"
                  >
                    <CustomDropDown
                      v-model="studentToAdd.others"
                      :options="collections.othersStudent.list"
                      :disabled="isReadOnly"
                      :clearable="false"
                      :error="isValidNeccesity(item)"
                      :custom-error-msg="errorMsgNoStudentsAdded"
                      label="Nombre de Estudiante:"
                      fieldkey="studentSie"
                      fieldname="name"
                      placeholder="Seleccione"
                      class="height-fixed"
                    />
                    <div
                      class="col-12 px-0 pt-3 text-right mb-2 d-flex align-self-end justify-content-end"
                    >
                      <ActionButton
                        v-if="!isReadOnly"
                        text="Añadir"
                        variant="success"
                        i="fas fa-file-plus"
                        class="add-tran"
                        @click="
                          addStudentNeccesity(
                            item,
                            studentToAdd.others,
                            collections.othersStudent.list,
                            () => (studentToAdd.others = null)
                          )
                        "
                      />
                    </div>
                  </div>
                </div>
                <div :class="['col-12', { 'col-md-8 mt-2': !isReadOnly }]">
                  <div class="row">
                    <div class="col-12 ml-3 chips-container">
                      <CustomChipsGroup
                        :selected-items="
                          getNecessity(item.id).studentNecessities
                        "
                        :disabled="isReadOnly"
                        title="Estudiantes seleccionados"
                        :no-background="true"
                        :no-wrap="true"
                        object-text="name"
                        @itemMarkedForDeletion="
                          (studentNecessity) =>
                            removeStudentNeccesity(
                              item,
                              studentNecessity,
                              collections.othersStudent.list
                            )
                        "
                      />
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </NecessityInformationForm>
        </div>
      </div>
      <Error
        v-if="needToCheckAtLeastOneNeed"
        variant="no-border"
        message="La solicitud debe contar con al menos una necesidad documentada en sistema."
        class="mt-3"
      />
    </ValidationObserver>

    <ValidationObserver ref="documents-information-form">
      <DocumentsForm
        class="mt-4"
        :documents="model.documents"
        :allowed-document-types="allowedDocumentTypes"
        :multiple-uploads-document-type-ids="[requestDocumentTypeCodes.HROtro]"
        :read-only="isReadOnly"
      >
        <template
          v-if="isSingleStudent && model.studentList[0].lastSignedPei"
          v-slot:default-documents
        >
          <div class="row box-container mb-3">
            <div class="col-6 col-md-5">
              <div class="row">
                <div class="col-12">
                  <span class="font-weight-normal">Tipo de Documento:</span>
                </div>
                <div class="col-12">
                  <span class="font-weight-regular"> Documento de PEI </span>
                </div>
              </div>
            </div>

            <div class="col-6 col-md-5">
              <div class="row">
                <div class="col-12">
                  <span class="font-weight-normal">Archivo:</span>
                </div>
                <div class="col-12">
                  <span class="font-weight-regular">
                    {{ model.studentList[0].lastSignedPei }}_PEIReport.pdf
                  </span>
                </div>
              </div>
            </div>

            <div class="col-12 d-block d-md-none py-2" />

            <div class="col-12 col-md-2 pt-2">
              <div
                class="h-100 d-md-flex d-lg-flex justify-content-end align-items-center"
              >
                <DropDown :items="defaultDocumentActions" class="d-sm-block" />
              </div>
            </div>
          </div>
        </template>
      </DocumentsForm>
    </ValidationObserver>

    <ValidationObserver ref="assistant-information-form">
      <AssistantInformation
        :assistants="model.schoolAssistants"
        :collections="collections"
        :submitted="isReadOnly"
        :has-assistant-available.sync="model.schoolHasAssistantsAvailable"
        :validate.sync="invalidAssistant"
        :show-submitted-warning-text="true"
        :use-header-title="true"
        class="mt-4 pb-4"
      />
    </ValidationObserver>

    <ValidationObserver ref="signature-information-form">
      <SchoolDirectorSignature
        title="Firma de Director Escolar"
        principal-button-label="Presione aquí para añadir firma"
        role-label="Puesto"
        class="mt-4"
        v-if="canShowSignaturePanel"
        :allow-add-more="allowAddSignature"
        :limit-new-signatures-to="1"
        :cannot-edit-name="false"
        :keep-signed="true"
        :use-role="false"
        :requesters="model.signers"
        :submitted="isReadOnly"
        :validate.sync="invalidSignature"
        :positions-list="requestersPositionsList"
      />
    </ValidationObserver>
  </div>
</template>

<script>
import _ from "lodash";
import SectionTitle from "SectionTitle";
import CustomCheckbox from "CheckBox";
import DropDown from "dropdown";
import CustomDropDown from "custom-drop-down";
import ActionButton from "ActionButton";
import CustomCallout from "custom-callout";
import Error from "custom-error";
import DocumentsForm from "@/views/humanResources/request/components/DocumentsForm";
import SchoolDirectorSignature from "@/components/pei/ubication-tech-assistance/_components/Requesters";
import AssistantInformation from "@/views/humanResources/request/components/tabs/components/shared/AssistantInformation";
import utilitiesService from "@/services/hr/utilities";
import generalService from "@/services/pei/general";
import documentNecessityConfigs from "@/utils/constants/hrrequest/documentNecessityConfigs";
import necessityCodes from "@/utils/constants/hrrequest/necessityCodes";
import {requestTypeCodes} from "@/utils/constants/hrrequest/requestTypeCodes";
import peiServiceNeedCodes from "@/utils/constants/hrrequest/peiServiceNeedCodes";
import requestDocumentTypeCodes from "@/utils/constants/hrrequest/requestDocumentTypeCodes";
import alert from "@/mixin/alert.mixin";
import { CustomChipsGroup } from "@/components/common";
import {
  Document,
  GeneralInformation,
  NecessityInformationForm,
  DocumentSelector,
} from "@/views/humanResources/request/components/tabs/components/tab1";
import { getPositions } from "@/services/api/tapRequest.api.js";

const errorMsgNoStudentsAdded = "Favor añadir un estudiante.";

export default {
  name: "Tab1RequestInformation",
  mixins: [alert],
  components: {
    GeneralInformation,
    SectionTitle,
    CustomCheckbox,
    AssistantInformation,
    NecessityInformationForm,
    Document,
    DropDown,
    CustomDropDown,
    ActionButton,
    CustomChipsGroup,
    DocumentsForm,
    SchoolDirectorSignature,
    CustomCallout,
    Error,
    DocumentSelector,
  },
  data() {
    return {
      peiServiceNeedCodes,
      necessityCodes,
      requestDocumentTypeCodes,
      errorMsgNoStudentsAdded,
      invalidSignature: false,
      invalidAssistant: false,
      healthAssistanceTypes: [],
      serviceAssistanceTypes: [],
      healthAssistanceTypeToAdd: null,
      serviceAssistanceTypeToAdd: null,
      studentToAdd: {
        hygiene: null,
        mobility: null,
        communication: null,
        feeding: null,
        health: null,
        transportation: null,
        others: null,
      },
      needToCheckAtLeastOneNeed: false,
      requestersPositionsList: [],
      defaultDocumentActions: [
        {
          label: "Descargar",
          icon: "fas fa-file-download text-green pr-1",
          click: () => this.getReportsPEIPS(),
        },
      ],
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      required: true,
      default: false,
    },
    isPhoneRequired: {
      type: Boolean,
      default: true,
    },
    areJustificationsRequired: {
      type: Boolean,
      default: true,
    },
    collections: {
      type: Object,
      required: true,
    },
    canShowSignaturePanel: {
      type: Boolean,
      default: true,
    },
    canShowNecessitySubSections: {
      type: Boolean,
      default: true,
    }
  },
  computed: {
    selectedItemTransportacion() {
      var studentNecessities = this.getNecessity(
        necessityCodes.transportation
      ).studentNecessities;
      var selectItems = [];

      studentNecessities.forEach((studentNecessity) => {
        var items = studentNecessity.assistantTypes.map((assistence) => ({
          student: studentNecessity.student,
          studentName: studentNecessity.student.name,
          assitence: assistence,
          assitenceName: assistence.name,
        }));
        selectItems = selectItems.concat(items);
      });
      return selectItems;
    },
    allowedDocumentTypes() {
      const documentTypeIds = [
        requestDocumentTypeCodes.HRMinuta,
        requestDocumentTypeCodes.HROtro,
      ];
      return this.collections.documentTypes.filter((docType) =>
        documentTypeIds.includes(docType.id)
      );
    },
    allowAddSignature() {
      return this.model.signers.every((x) => x.isSubmitted);
    },
    model() {
      return this.value;
    },
    isSingleStudent() {
      return this.value.assistantTypeId == requestTypeCodes.Estudiantes;
    },
    isTwoMoreStudent() {
      return this.value.assistantTypeId == requestTypeCodes.DosMasEstudiantes;
    },
    isGroupStudent() {
      return this.value.assistantTypeId == requestTypeCodes.Grupo;
    },
    isMobile() {
      return this.$store.getters.getIsMobileDensity;
    },
    isValidNeccesity() {
      return (item) => {
        const neccesity = this.getNecessity(item.id);
        if (neccesity.submitted && !neccesity.studentNecessities.length)
          return this.errorMsgNoStudentsAdded;
        else return null;
      };
    },
  },
  methods: {
    async necessitiesChange(selected, necessity) {
      if (necessity.isForceChecked) {
        necessity.isForceChecked = !necessity.isForceChecked;
        return;
      }
      if (selected) {
        let studentHaveServiceNeeds = !!this.getStudentsByNeeds(necessity.id)
          .length;
        if (
          !studentHaveServiceNeeds &&
          necessity.id !== necessityCodes.others &&
          !this.isGroupStudent
        ) {
          this.ShowToast(
            "Alerta",
            this.collections.resources["R01210002"],
            "error"
          );
          this.setCheckNecessity(necessity, false);
          return;
        }

        let studentNecessities = [];
        if (this.isSingleStudent) {
          const student = this.model.studentList[0];
          const documents = this.getNecessityDocuments(necessity);
          let studentNecessity = {
            student,
            studentSieId: student.studentSie,
            name: student.name,
            documents,
            assistantTypesId: [],
            assistantTypes: [],
          };
          studentNecessities.push(studentNecessity);
        }

        this.model.necessities.push({
          necessityId: necessity.id,
          name: necessity.name,
          studentNecessities,
          submitted: false,
        });

        this.fillStudentCollections(necessity, studentNecessities);
      } else {
        if (this.isNecessityDocumented(necessity.id)) {
          const response = await this.ShowCancelModal(
            "Confirmación",
            `Usted cuenta con información registrada. ¿Está seguro que desea eliminar la información para la necesidad de <b>${necessity.name}</b>?`,
            "Sí",
            "No"
          );
          if (!response) {
            this.setCheckNecessity(necessity, true);
            return;
          }
          this.ShowToast(
            "Confirmación",
            "La información ha sido eliminada exitosamente.",
            "success"
          );
        }
        this.model.necessities = this.model.necessities.filter(
          (n) => n.necessityId !== necessity.id
        );
      }
      necessity.selected = selected;

      if (!this.isGroupStudent)
        this.needToCheckAtLeastOneNeed = !this.collections.necessities.some(
          (x) => x.selected
        );
    },
    fillStudentCollections(necessity, studentNecessities) {
      const studentSieIds = studentNecessities.map((sn) => sn.studentSieId);
      if (
        necessity.id == necessityCodes.health ||
        necessity.id == necessityCodes.transportation
      ) {
        const needs = (student) =>
          this.getAssistanceTypes(
            necessity,
            student.studentSie,
            this.getServiceNeeds(student.serviceNeeds, necessity.id)
          );
        const students = this.getStudentsByNeeds(necessity.id).filter(
          (student) => {
            const assistantTypes = needs(student);
            return assistantTypes && assistantTypes.length;
          }
        );
        switch (necessity.id) {
          case necessityCodes.health: {
            this.collections.healthStudent.list = students;
            if (this.isSingleStudent)
              this.healthAssistanceTypes = needs(this.model.studentList[0]);
            break;
          }
          case necessityCodes.transportation: {
            this.collections.transportationStudent.list = students;
            if (this.isSingleStudent)
              this.serviceAssistanceTypes = needs(this.model.studentList[0]);
            break;
          }
        }
      } else if (necessity.id == necessityCodes.others) {
        this.collections.othersStudent.list = this.model.studentList.filter(
          (s) => !studentSieIds.includes(s.studentSie)
        );
      } else {
        const students = this.getStudentsByNeeds(necessity.id).filter(
          (s) => !studentSieIds.includes(s.studentSie)
        );
        for (const key in this.collections) {
          if (
            this.collections.hasOwnProperty(key) &&
            this.collections[key].id === necessity.id
          ) {
            this.collections[key].list = students;
            break;
          }
        }
      }
    },
    isNecessityDocumented(necessityId) {
      const necessity = this.getNecessity(necessityId);
      return (
        necessity &&
        (necessity.justification ||
          necessity.studentNecessities.some(
            (sn) =>
              (!this.isSingleStudent && sn.studentSieId) ||
              sn.documents.some((d) => d.file) ||
              sn.assistantTypesId.some((id) => id)
          ))
      );
    },
    cleanDropDownsSelection(student) {
      for (const key in this.studentToAdd) {
        if (
          Object.hasOwnProperty.call(this.studentToAdd, key) &&
          this.studentToAdd[key] &&
          this.studentToAdd[key].studentSie === student.studentSie
        ) {
          this.studentToAdd[key] = null;
        }
      }
    },
    setCheckNecessity(necessity, checked) {
      const refName = `checkbox-${necessity.name}`;
      necessity.isForceChecked = true;
      necessity.selected = checked;
      this.$refs[refName][0].model = checked;
      document.getElementById(this.$refs[refName][0].id).checked = checked;
    },
    getStudentsByNeeds(serviceNeedCategoryId) {
      return this.model.studentList.filter((student) =>
        student.serviceNeeds.some(
          (sn) => sn.serviceNeedCategoryId == serviceNeedCategoryId
        )
      );
    },
    getServiceNeeds(serviceNeeds, serviceNeedCategoryId) {
      return serviceNeeds
        .filter((sn) => sn.serviceNeedCategoryId == serviceNeedCategoryId)
        .map((sn) => ({ id: sn.id, name: sn.spaShortDescription }));
    },
    getNecessity(id) {
      return this.value.necessities.find((n) => n.necessityId == id);
    },
    getDocumentType(documentTypeId) {
      return (
        this.collections.documentTypes.find(
          (docType) => docType.id === documentTypeId
        ) || {}
      );
    },
    getNecessityDocuments(necessity) {
      const documents = [];
      const documentConfig = documentNecessityConfigs.find(
        (config) => config.necessityId == necessity.id
      );
      if (documentConfig) {
        documentConfig.documentTypes.forEach((docType) => {
          const document = utilitiesService.createDocument
            .bind(this, docType.id, docType.rules)
            .call();
          documents.push(document);
        });
      }
      return documents;
    },
    getAssistanceTypes(necessity, studentSie, assistanceTypesCollection) {
      const studentNecessities = this.getNecessity(
        necessity.id
      ).studentNecessities;

      let studentNecessity = studentNecessities.find(
        (sn) => sn.studentSieId == studentSie
      );

      if (studentNecessity) {
        return assistanceTypesCollection.filter(
          (i) => !studentNecessity.assistantTypesId.includes(i.id)
        );
      } else return [...assistanceTypesCollection];
    },
    addStudentNeccesity(necessity, student, studentCollection, cleanStudent) {
      if (!student) return;

      const studentNecessities = this.getNecessity(
        necessity.id
      ).studentNecessities;
      const documents = this.getNecessityDocuments(necessity);
      studentNecessities.push({
        student,
        studentSieId: student.studentSie,
        name: student.name,
        documents,
      });
      const index = studentCollection.findIndex(
        (e) => e.studentSie === student.studentSie
      );
      studentCollection.splice(index, 1);
      if (cleanStudent) cleanStudent();
    },
    async removeStudentNeccesity(
      necessity,
      studentNecessity,
      studentCollection
    ) {
      if (!necessity || !studentNecessity || !studentCollection) return;

      if (necessity.id == necessityCodes.health) {
        const response = await this.ShowCancelModal(
          "Confirmación",
          `¿Está seguro que desea eliminar el estudiante de la necesidad de ${necessity.name}? `
        );
        if (!response) return;
      }

      const studentNecessities = this.getNecessity(
        necessity.id
      ).studentNecessities;

      const index = studentNecessities.findIndex(
        (e) => e.studentSieId === studentNecessity.studentSieId
      );
      studentNecessities.splice(index, 1);

      studentCollection.push({
        ...studentNecessity.student,
        studentSie: studentNecessity.studentSieId,
      });

      this.ShowToast(
        "Proceso exitoso.",
        "El estudiante ha sido removido de la necesidad exitosamente",
        "success"
      );
    },
    addStudentNeccesityWithAssistance(
      necessity,
      student,
      studentCollection,
      assistance,
      assistanceCollection,
      cleanModel
    ) {
      if (!student || !assistance) return;

      const studentNecessities = this.getNecessity(
        necessity.id
      ).studentNecessities;

      let studentNecessity = studentNecessities.find(
        (sn) => sn.studentSieId == student.studentSie
      );

      if (studentNecessity) {
        studentNecessity.assistantTypesId.push(assistance.id);
        studentNecessity.assistantTypes.push({
          id: assistance.id,
          name: assistance.name,
        });
      } else {
        const documents = this.getNecessityDocuments(necessity);
        studentNecessity = {
          student,
          studentSieId: student.studentSie,
          name: student.name,
          documents,
          assistantTypesId: [assistance.id],
          assistantTypes: [
            {
              id: assistance.id,
              name: assistance.name,
            },
          ],
        };
        studentNecessities.push(studentNecessity);
      }

      const index = assistanceCollection.findIndex(
        (e) => e.id === assistance.id
      );
      assistanceCollection.splice(index, 1);

      // Remove student if all Assistant were added
      if (
        studentCollection &&
        studentCollection.length &&
        !assistanceCollection.length
      ) {
        const index = studentCollection.findIndex(
          (e) => e.studentSie === student.studentSie
        );
        studentCollection.splice(index, 1);
      }

      if (cleanModel) cleanModel();
    },
    removeStudentNeccesityWithAssistance(
      necessity,
      student,
      studentCollection,
      assistance,
      assistanceCollection
    ) {
      if (!necessity || !student || !assistance) return;

      const studentNecessities = this.getNecessity(
        necessity.id
      ).studentNecessities;

      let studentNecessity = studentNecessities.find(
        (sn) => sn.studentSieId == student.studentSie
      );

      studentNecessity.assistantTypesId =
        studentNecessity.assistantTypesId.filter((e) => e !== assistance.id);
      const index = studentNecessity.assistantTypes.findIndex(
        (e) => e.id === assistance.id
      );
      studentNecessity.assistantTypes.splice(index, 1);

      if (assistanceCollection) assistanceCollection.push(assistance);

      // Add student if was removed because all his Assistant were added
      if (
        studentCollection &&
        !studentCollection.some((x) => x.studentSie == student.studentSie)
      )
        studentCollection.push(student);
    },
    toggleCallout(id) {
      const callout = document.getElementById(id);

      const toggleIcon = callout.querySelector("em");
      const expandedContainer = callout.querySelector(
        ".callout-expanded-container"
      );

      if (expandedContainer.style.display === "none") {
        expandedContainer.style.display = "none";
        expandedContainer.style.display = "";

        toggleIcon.classList.remove(
          "fa-chevron-circle-right",
          "color-primary-2"
        );
        toggleIcon.classList.add("fa-chevron-circle-down", "color-primary-1");
      } else {
        expandedContainer.style.display = "";
        expandedContainer.style.display = "none";

        toggleIcon.classList.remove(
          "fa-chevron-circle-down",
          "color-primary-1"
        );
        toggleIcon.classList.add("fa-chevron-circle-right", "color-primary-2");
      }
    },
    async validateForm() {
      let hasAtLeastOneNeccessityChecked = true;
      this.model.necessities.map((neccesity) => (neccesity.submitted = true));
      this.invalidSignature = this.canShowSignaturePanel ? this.allowAddSignature : false;
      this.invalidAssistant =
        this.model.schoolHasAssistantsAvailable == null ||
        (this.model.schoolHasAssistantsAvailable &&
          !this.model.schoolAssistants.length);

      const isValidAllNeccessityCheckedToAddStudent = this.isGroupStudent
        ? true
        : this.model.necessities.every(
            (neccesity) => neccesity.studentNecessities.length
          );

      if (!this.isGroupStudent) {
        hasAtLeastOneNeccessityChecked = this.collections.necessities.some(
          (s) => s.selected
        );
        this.needToCheckAtLeastOneNeed = !hasAtLeastOneNeccessityChecked;
      }

      const isValidRequestGeneralInformation = await this.$refs[
        "general-information-form"
      ].validate();
      const isValidAssistantInformation = await this.$refs[
        "assistant-information-form"
      ].validate();
      const isValidDocumentsInformation = await this.$refs[
        "documents-information-form"
      ].validate();
      const isValidRequestInformation = await this.$refs[
        "request-information-form"
      ].validate();
      const isValidSignature = await this.$refs[
        "signature-information-form"
      ].validate();

      const isValidDocumentFeeding = this.isGroupStudent
        ? true
        : this.validatedRequiredDocs();

      return (
        isValidAllNeccessityCheckedToAddStudent &&
        isValidRequestGeneralInformation &&
        isValidAssistantInformation &&
        isValidDocumentsInformation &&
        isValidRequestInformation &&
        !this.invalidSignature &&
        !this.invalidAssistant &&
        isValidSignature &&
        hasAtLeastOneNeccessityChecked &&
        isValidDocumentFeeding
      );
    },
    getStudentsWithoutNeeds() {
      if (!this.isSingleStudent) {
        const checkedNecessities = this.collections.necessities.filter(
          (s) => s.selected
        );

        let _students = [];
        checkedNecessities.forEach((item) => {
          if (item.id === necessityCodes.hygiene) {
            _students = [..._students, ...this.collections.hygieneStudent.list];
          }
          if (item.id === necessityCodes.feeding) {
            _students = [..._students, ...this.collections.feedingStudent.list];
          }
          if (item.id === necessityCodes.movility) {
            _students = [
              ..._students,
              ...this.collections.mobilityStudent.list,
            ];
          }
          if (item.id === necessityCodes.comunication) {
            _students = [
              ..._students,
              ...this.collections.communicationStudent.list,
            ];
          }
          if (item.id === necessityCodes.health) {
            _students = [..._students, ...this.collections.healthStudent.list];
          }
          if (item.id === necessityCodes.transportation) {
            _students = [
              ..._students,
              ...this.collections.transportationStudent.list,
            ];
          }
        });
        return _.uniq(_students, "studentSie");
      } else {
        return [];
      }
    },
    async getPositions() {
      const { data } = await getPositions(17);
      this.requestersPositionsList = data;
    },
    async onSelectedDocumentHandler(studentNecessity, selectedDocumentItem) {
      studentNecessity.documents.map((doc) => {
        if (
          doc.documentTypeId ===
          selectedDocumentItem.selectedDocumentType.documentTypeId
        ) {
          doc.file = selectedDocumentItem.file;
          if (!doc.file) {
            doc.added = null;
            doc.filename = null;
            doc.itemAttachmentId = null;
          }
        }
        return doc;
      });
      await this.$forceUpdate();
    },
    validatedRequiredDocs() {
      let validations = [];
      const _docRef = this.$refs.documentSelectorRef;
      if (_docRef?.length > 0) {
        for (let i = 0; i <= _docRef.length - 1; i++) {
          const _isValid = _docRef[i].validate();
          validations.push(_isValid);
        }
      }
      return !validations.includes(false);
    },
    getReportsPEIPS() {
      const student = this.model.studentList[0];
      let peiId = student.lastSignedPei;
      let self = this;

      let promise = new Promise((resolve) => {
        self
          .$ApiGet("/api/report/pei/" + peiId)
          .then((res) => {
            self.report = res.data;
            generalService.documentDownloadReport(
              self.report.content,
              self.report.filename
            );
            resolve(res.data);
          })
          .catch((err) => {
            self.response = err;
          });
      });
      return promise;
    },
    canShowNecessityInformationForm(id, necessityCode) {
      const necessity = this.getNecessity(id);
      const isCurrentNecessity = id === necessityCode && necessity;
      if (this.canShowNecessitySubSections) {
        return isCurrentNecessity;
      }
      return isCurrentNecessity && !!necessity.justification
    },
  },
  async mounted() {
    await this.getPositions();

    const isEditing =
      this.$route.params.id &&
      this.model.id &&
      this.model.necessities &&
      this.model.necessities.length;
    if (isEditing) {
      this.model.necessities.forEach((requestNecessity) => {
        const necessity = this.collections.necessities.find(
          (necessity) => necessity.id === requestNecessity.necessityId
        );
        this.setCheckNecessity(necessity, true);
        this.fillStudentCollections(
          necessity,
          requestNecessity.studentNecessities
        );
      });
    }
  },
};
</script>

<style scoped>
.divider-horizontal {
  border-right: 1px solid #e2e2e2;
  height: 100%;
}
.divider-vertical {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-top: 1px solid #e2e2e2;
}
.form-divider {
  border: 1px solid #e2e2e2;
}
::v-deep .chips-container .bold {
  font-weight: 400 !important;
}

::v-deep .bg-white .bg-secondary-3,
.white-container {
  background-color: #fff;
}

::v-deep .custom-ddl-error-no-height .student-search-alert-error {
  height: 0px;
}

.grey-background {
  background-color: #f7f7f7;
}

.assistant-type-label {
  color: var(--gray);
}

@media only screen and (max-width: 375px) {
  ::v-deep .add-tran {
    width: 100% !important;
  }
  ::v-deep .add-health-assistance {
    width: 100% !important;
  }
}
.info-text {
  font-weight: bold;
}
.grey-text {
  color: var(--gray);
}
</style>
